import { Component, EventEmitter, Input, Output } from '@angular/core';
import { catchError, tap } from 'rxjs/operators';
import { saveAs } from 'file-saver';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { defaultPageNumber, Document, Folder, Subfolder } from '../../models/models';
import { DocumentService } from '../../services/document.service';
import { FolderEditComponent } from '../folder-edit/folder-edit.component';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable
} from '@angular/material/table';
import { TableDataPipe } from './pipes/table-data.pipe';
import { RouterLink } from '@angular/router';
import { FileIconPipe } from '../../../../pipes/file-icon.pipe';
import { DatePipe } from '@angular/common';
import { FileSizePipe } from './pipes/file-size.pipe';
import { MatIcon } from '@angular/material/icon';
import { openConfirmationDialog } from '../../../../components/confirmation-popup/confirmation-popup';
import { filter, NEVER, switchMap } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FolderService } from '../../services/folder.service';
import { HttpErrorResponse } from '@angular/common/http';
import { userTimezone } from '../../../../models/date-formats';

@Component({
  selector: 'app-folder',
  templateUrl: './folder.component.html',
  styleUrls: ['./folder.component.scss'],
  imports: [
    MatPaginator,
    MatTable,
    TableDataPipe,
    MatColumnDef,
    MatHeaderCell,
    MatCell,
    MatCellDef,
    RouterLink,
    FileIconPipe,
    MatHeaderCellDef,
    DatePipe,
    FileSizePipe,
    MatHeaderRow,
    MatHeaderRowDef,
    MatRow,
    MatRowDef,
    MatIcon
  ],
  standalone: true
})
export class FolderComponent {
  @Input() folder: Folder | null = null;
  @Input() pageNumber = defaultPageNumber;
  @Output() pageChanged = new EventEmitter<number>();
  readonly displayedColumns = ['name', 'uploadedBy', 'uploadedAt', 'company', 'category', 'size', 'actions'];
  readonly userTimezone = userTimezone;

  constructor(
    private documentService: DocumentService,
    private matDialog: MatDialog,
    private matSnackBar: MatSnackBar,
    private folderService: FolderService
  ) {}

  download(file: Document) {
    this.documentService
      .download(file.id)
      .pipe(tap(blob => saveAs(blob, file.name)))
      .subscribe();
  }

  deleteFileBy(id: number) {
    openConfirmationDialog(this.matDialog, {
      title: 'Delete file',
      message: 'Are you sure you want to remove this file?'
    })
      .afterClosed()
      .pipe(
        filter(result => result === 'Confirmed'),
        switchMap(() =>
          this.documentService.delete(id).pipe(
            tap(() => {
              this.matSnackBar.open('The file was deleted successfully', 'x', { panelClass: 'snackbar-green' });
              this.folder = { ...this.folder!, files: this.folder!.files.filter(file => file.id !== id) };
            }),
            catchError(error => this.handleDeleteError(error, 'file'))
          )
        )
      )
      .subscribe();
  }

  deleteFolderBy(id: number) {
    openConfirmationDialog(this.matDialog, {
      title: 'Delete folder',
      message: 'Are you sure you want to remove this folder?\n All files and folders inside will be removed too.'
    })
      .afterClosed()
      .pipe(
        filter(result => result === 'Confirmed'),
        switchMap(() =>
          this.folderService.delete(id).pipe(
            tap(() => {
              this.matSnackBar.open('The folder was deleted successfully', 'x', { panelClass: 'snackbar-green' });
              this.folder = { ...this.folder!, folders: this.folder!.folders.filter(folder => folder.id !== id) };
            }),
            catchError(error => this.handleDeleteError(error, 'folder'))
          )
        )
      )
      .subscribe();
  }

  folderEditBy(id: number) {
    this.matDialog
      .open(FolderEditComponent, { minWidth: '300px', data: { id } })
      .afterClosed()
      .pipe(
        tap((result: Subfolder) => {
          if (result) {
            this.folder = {
              ...this.folder!,
              folders: this.folder!.folders.map(folder => {
                return folder.id === result.id ? result : folder;
              })
            };
          }
        })
      )
      .subscribe();
  }

  onPageChange(pageEvent: PageEvent) {
    this.pageChanged.emit(pageEvent.pageIndex + 1);
  }

  private handleDeleteError(error: HttpErrorResponse, entity: 'file' | 'folder') {
    this.matSnackBar.open(`The ${entity} could not be deleted. Please try again later.`, 'x');
    console.error(error);
    return NEVER;
  }
}
